/**
 * @file        src/components/BrandSelect.jsx
 * @description Brand dropdown fed by the tenant's `brands` registry (GET
 *              /api/brands) — never a hardcoded list. `withAll` prepends an
 *              "All brands" option (value '') for filter bars; forms omit it
 *              so a creator must pick a real storefront (no default brand).
 * @author      Kinetika Labs
 * @created     2026-08-03
 * @changelog
 *   - 2026-08-03 Claude — Initial (registry-driven brands).
 *   - 2026-09-12 Claude — A tenant with exactly ONE registered brand now gets
 *                          it preselected in form dropdowns. The dropdown is
 *                          still rendered — the storefront a record belongs to
 *                          stays visible, and a second brand can appear at any
 *                          time — it just stops asking for a choice that has
 *                          one possible answer, which every single-brand tenant
 *                          had to fill in by hand on each form and could fail
 *                          validation on. This does NOT reintroduce a default
 *                          brand: the value comes from the tenant's own
 *                          registry, and the moment a second brand exists the
 *                          field goes back to demanding an explicit pick.
 *                          Filter bars (withAll) are untouched — "All brands"
 *                          stays their default.
 */
import { useEffect } from 'react';
import Select from './Select';
import { useBrands } from '../api/brands';

export default function BrandSelect({
  value,
  onChange,
  withAll = false,
  allLabel = 'All brands',
  invalid = false,
  placeholder = 'Select brand',
}) {
  const { options, isLoading } = useBrands();
  const opts = withAll ? [{ value: '', label: allLabel }, ...options] : options;

  const soleBrand = !withAll && options.length === 1 ? options[0].value : null;
  useEffect(() => {
    // Runs once per empty field: setting the value makes `value` truthy, which
    // is the guard, so an inline onChange identity change can't loop.
    if (soleBrand && !value) onChange(soleBrand);
  }, [soleBrand, value, onChange]);

  return (
    <Select
      value={value}
      onChange={onChange}
      options={opts}
      invalid={invalid}
      placeholder={isLoading ? 'Loading…' : placeholder}
    />
  );
}
